import { zodResolver } from '@hookform/resolvers/zod';
import { format, parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';
import {
  type FieldValues,
  type Path,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Button,
  DatePicker,
  Form,
  FormField,
  FormFieldCurrencyInput,
  FormFieldDropdownSingleSelect,
  FormFieldInput,
  FormFieldToggle,
  Icon,
  type IconName,
  Tooltip,
} from '~/chords';
import { Breadcrumbs } from '~/chords/Breadcrumbs';

import { useAdvertisers } from '../../api/useAdvertisers';
import { useRestrictions } from '../../api/useRestrictions';
import { formatUsd } from '../../utils/formatters';
import {
  type CreateCampaignFormValues,
  createCampaignSchema,
  restrictionsToSchema,
} from './createCampaignSchema';

const DEFAULT_VALUES: CreateCampaignFormValues = {
  campaignStartDate: '',
  campaignEndDate: '',
  advertiser: '',
  artist: '',
  track: '',
  isrc: '',
  spotifyUrl: '',
  tiktokLink: '',
  instagramLink: '',
  clippingEnabled: true,
  clippingBudget: undefined,
  clippingPurchaseOrder: '',
  influencerEnabled: true,
  influencerBudget: undefined,
  influencerPurchaseOrder: '',
};

function InfoTooltip({ content }: { content: string }) {
  return (
    <Tooltip content={content} size="sm">
      <span>
        <Icon type="circle-info" size="sm" />
      </span>
    </Tooltip>
  );
}

InfoTooltip.displayName = 'InfoTooltip';

function SectionHeading({
  icon,
  children,
}: {
  icon: IconName;
  children: React.ReactNode;
}) {
  return (
    <h2 className="flex items-center gap-sm text-headline-sm font-semibold">
      <Icon type={icon} size="md" />
      {children}
    </h2>
  );
}

SectionHeading.displayName = 'SectionHeading';

function FormFieldDatePicker<TFormFields extends FieldValues>({
  name,
  label,
  placeholder,
  minDate,
}: {
  name: Path<TFormFields>;
  label: string;
  placeholder?: string;
  minDate?: Date;
}) {
  const { field } = useController<TFormFields>({ name });

  const dateValue = field.value ? parseISO(field.value as string) : undefined;

  const handleApply = useCallback(
    (date: Date) => {
      field.onChange(format(date, 'yyyy-MM-dd'));
    },
    [field],
  );

  return (
    <FormField<TFormFields> name={name} label={label} labelAsSpan>
      <DatePicker
        value={dateValue}
        onApply={handleApply}
        placeholder={placeholder}
        minDate={minDate}
        allowFutureDates
        highlightToday
      />
    </FormField>
  );
}

FormFieldDatePicker.displayName = 'FormFieldDatePicker';

export function CreateCampaignPage() {
  const { t } = useTranslation('marketing', {
    keyPrefix: 'automatedCreatorCampaign',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { advertisers, isLoading: isLoadingAdvertisers } = useAdvertisers();
  const { restrictions } = useRestrictions();

  const restoredValues = (
    location.state as { formValues?: CreateCampaignFormValues } | null
  )?.formValues;

  const advertiserOptions = useMemo(
    () => advertisers.map((name) => ({ value: name, label: name })),
    [advertisers],
  );

  const schemaRestrictions = useMemo(
    () => (restrictions ? restrictionsToSchema(restrictions) : undefined),
    [restrictions],
  );

  const schema = useMemo(
    () => createCampaignSchema(schemaRestrictions),
    [schemaRestrictions],
  );

  const formHook = useForm<CreateCampaignFormValues>({
    resolver: zodResolver(schema),
    defaultValues: restoredValues ?? DEFAULT_VALUES,
    mode: 'onSubmit',
  });

  const clippingEnabled = useWatch({
    control: formHook.control,
    name: 'clippingEnabled',
  });
  const influencerEnabled = useWatch({
    control: formHook.control,
    name: 'influencerEnabled',
  });
  const clippingBudget = useWatch({
    control: formHook.control,
    name: 'clippingBudget',
  });
  const influencerBudget = useWatch({
    control: formHook.control,
    name: 'influencerBudget',
  });

  const clippingSpend = useMemo(
    () =>
      clippingEnabled && clippingBudget ? formatUsd(clippingBudget) : '--',
    [clippingEnabled, clippingBudget],
  );

  const influencerSpend = useMemo(
    () =>
      influencerEnabled && influencerBudget
        ? formatUsd(influencerBudget)
        : '--',
    [influencerEnabled, influencerBudget],
  );

  const totalSpend = useMemo(() => {
    const clipping = clippingEnabled ? (clippingBudget ?? 0) : 0;
    const influencer = influencerEnabled ? (influencerBudget ?? 0) : 0;
    const total = clipping + influencer;

    return total > 0 ? formatUsd(total) : '--';
  }, [clippingEnabled, influencerEnabled, clippingBudget, influencerBudget]);

  const handleSubmit = (values: CreateCampaignFormValues) => {
    navigate('/marketing/automated-creator-campaigns/new/review', {
      state: { formValues: values },
    });
  };

  return (
    <div className="max-w-[2048px] p-xl">
      <Breadcrumbs.Root>
        <Breadcrumbs.Item to="/marketing">
          {t('breadcrumb.marketing')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item to="/marketing/automated-creator-campaigns">
          {t('breadcrumb.current')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item>{t('createCampaign.breadcrumb')}</Breadcrumbs.Item>
      </Breadcrumbs.Root>

      <h1 className="mt-md text-display-sm font-bold">
        {t('createCampaign.title')}
      </h1>

      <Form formHook={formHook} onSubmit={handleSubmit} schema={schema}>
        <section className="mt-xl rounded-sm border bg-emphasis-default border-emphasis-subtle">
          {/* Campaign details + Track details + Links */}
          <div className="grid grid-cols-3 gap-xl p-lg">
            <div className="flex flex-col gap-lg border-r pr-xl border-emphasis-subtle">
              <SectionHeading icon="calendar-check">
                {t('createCampaign.sections.campaignDetails')}
              </SectionHeading>

              <FormFieldDatePicker<CreateCampaignFormValues>
                name="campaignStartDate"
                label={t('createCampaign.fields.startDate')}
                placeholder={t('createCampaign.fields.startDatePlaceholder')}
              />
              <FormFieldDatePicker<CreateCampaignFormValues>
                name="campaignEndDate"
                label={t('createCampaign.fields.endDate')}
                placeholder={t('createCampaign.fields.endDatePlaceholder')}
              />

              <FormFieldDropdownSingleSelect<unknown, CreateCampaignFormValues>
                name="advertiser"
                label={t('createCampaign.fields.advertiser')}
                placeholder={t('createCampaign.fields.advertiserPlaceholder')}
                options={advertiserOptions}
                disabled={isLoadingAdvertisers}
              />
            </div>

            <div className="flex flex-col gap-lg border-r pr-xl border-emphasis-subtle">
              <SectionHeading icon="music">
                {t('createCampaign.sections.trackDetails')}
              </SectionHeading>

              <FormFieldInput<CreateCampaignFormValues>
                name="artist"
                label={t('createCampaign.fields.artist')}
                placeholder={t('createCampaign.fields.artistPlaceholder')}
              />

              <FormFieldInput<CreateCampaignFormValues>
                name="track"
                label={t('createCampaign.fields.track')}
                placeholder={t('createCampaign.fields.trackPlaceholder')}
              />

              <FormFieldInput<CreateCampaignFormValues>
                name="isrc"
                label={t('createCampaign.fields.isrc')}
                placeholder={t('createCampaign.fields.isrcPlaceholder')}
                labelAddon={
                  <InfoTooltip content={t('createCampaign.tooltips.isrc')} />
                }
              />
            </div>

            <div className="flex flex-col gap-lg">
              <SectionHeading icon="link">
                {t('createCampaign.sections.links')}
              </SectionHeading>

              <FormFieldInput<CreateCampaignFormValues>
                name="spotifyUrl"
                label={t('createCampaign.fields.spotifyUrl')}
                placeholder={t('createCampaign.fields.spotifyUrlPlaceholder')}
              />

              <FormFieldInput<CreateCampaignFormValues>
                name="tiktokLink"
                label={t('createCampaign.fields.tiktokLink')}
                placeholder={t('createCampaign.fields.tiktokLinkPlaceholder')}
              />

              <FormFieldInput<CreateCampaignFormValues>
                name="instagramLink"
                label={t('createCampaign.fields.instagramLink')}
                placeholder={t(
                  'createCampaign.fields.instagramLinkPlaceholder',
                )}
              />
            </div>
          </div>

          <hr className="border-emphasis-subtle" />

          {/* Channel and budget */}
          <div className="flex flex-col gap-lg p-lg">
            <SectionHeading icon="circle-dollar">
              {t('createCampaign.sections.channelAndBudget')}
            </SectionHeading>

            <div className="grid grid-cols-[1fr_1fr_auto] gap-xl">
              {/* Clipping channel */}
              <div className="flex flex-col gap-lg border-r pr-xl border-emphasis-subtle">
                <FormFieldToggle<CreateCampaignFormValues>
                  name="clippingEnabled"
                  label={t('createCampaign.fields.clippingCampaign')}
                  layout="horizontal"
                />
                <p className="text-body-sm text-subtle">
                  {t('createCampaign.fields.clippingDescription')}
                </p>
                <FormFieldCurrencyInput<CreateCampaignFormValues>
                  name="clippingBudget"
                  label={t('createCampaign.fields.spend')}
                  prefix="$ "
                  placeholder={t('createCampaign.fields.clippingMinimumSpend', {
                    amount: formatUsd(
                      schemaRestrictions?.minClippingBudget ?? 500,
                    ),
                  })}
                  disabled={!clippingEnabled}
                  decimalsLimit={2}
                />
                <FormFieldInput<CreateCampaignFormValues>
                  name="clippingPurchaseOrder"
                  label={t('createCampaign.fields.clippingPurchaseOrder')}
                  placeholder={t(
                    'createCampaign.fields.purchaseOrderNumberPlaceholder',
                  )}
                  disabled={!clippingEnabled}
                  labelAddon={
                    <InfoTooltip
                      content={t('createCampaign.tooltips.purchaseOrderNumber')}
                    />
                  }
                />
              </div>

              {/* Influencer channel */}
              <div className="flex flex-col gap-lg border-r pr-xl border-emphasis-subtle">
                <FormFieldToggle<CreateCampaignFormValues>
                  name="influencerEnabled"
                  label={t('createCampaign.fields.influencerCampaign')}
                  layout="horizontal"
                />
                <p className="text-body-sm text-subtle">
                  {t('createCampaign.fields.influencerDescription')}
                </p>
                <FormFieldCurrencyInput<CreateCampaignFormValues>
                  name="influencerBudget"
                  label={t('createCampaign.fields.spend')}
                  prefix="$ "
                  placeholder={t(
                    'createCampaign.fields.influencerMinimumSpend',
                    {
                      amount: formatUsd(
                        schemaRestrictions?.minInfluencerBudget ?? 2000,
                      ),
                    },
                  )}
                  disabled={!influencerEnabled}
                  decimalsLimit={2}
                />
                <FormFieldInput<CreateCampaignFormValues>
                  name="influencerPurchaseOrder"
                  label={t('createCampaign.fields.influencerPurchaseOrder')}
                  placeholder={t(
                    'createCampaign.fields.purchaseOrderNumberPlaceholder',
                  )}
                  disabled={!influencerEnabled}
                  labelAddon={
                    <InfoTooltip
                      content={t('createCampaign.tooltips.purchaseOrderNumber')}
                    />
                  }
                />
              </div>

              {/* Spend summary */}
              <div className="flex w-[180px] flex-col justify-end gap-md">
                <div className="flex flex-col gap-xs">
                  <span className="text-body-sm text-subtle">
                    {t('createCampaign.fields.clippingSpend')}
                  </span>
                  <span className="text-headline-sm font-semibold">
                    {clippingSpend}
                  </span>
                </div>
                <div className="flex flex-col gap-xs">
                  <span className="text-body-sm text-subtle">
                    {t('createCampaign.fields.influencerSpend')}
                  </span>
                  <span className="text-headline-sm font-semibold">
                    {influencerSpend}
                  </span>
                </div>
                <div className="flex flex-col gap-xs rounded-sm p-md text-right bg-emphasis-subtle">
                  <span className="text-body-sm font-medium">
                    {t('createCampaign.fields.totalSpend')}
                  </span>
                  <span className="text-headline-sm font-semibold">
                    {totalSpend}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Actions */}
        <div className="mt-xl flex justify-end">
          <Button type="submit" variant="primary" size="large">
            {t('createCampaign.actions.next')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

CreateCampaignPage.displayName = 'CreateCampaignPage';
